import { SystemCamera } from '../System/systemCamera';
import { Timeline } from '~src/System/systemTimeline';
import {
    DEFAULTSCROLLOPTIONS, EScrollAlignment, IScroll, ScrollEvent, ScrollPath
} from './scrollPath';
import { EScrollPath } from './scrollPathFunction';

export class ScrollCatcher extends ScrollPath {
    private animation: Timeline;

    constructor(system: SystemCamera, browser: HTMLElement) {
        super(system, browser);
        this.animation = new Timeline(system, 'scroll', { ease: 'power4.out' });
    }

    public set options(options: IScroll) {
        if (!options) options = DEFAULTSCROLLOPTIONS;
        this.setCatchSpeed(options.catchSpeed);
        this.setAlignment(options.alignment);
        this.setPath(options.path);
    }

    public get options(): IScroll {
        return {
            ...this._options,
        };
    }

    public setCatchSpeed(catchSpeed = this._options.catchSpeed) {
        this._options.catchSpeed = catchSpeed;
    }

    public setAlignment(alignment: EScrollAlignment = EScrollAlignment.VERTICAL) {
        this._options.alignment = alignment;
        this._setAlignment(alignment);
        this.resetScroll();
    }

    public setPath(path: EScrollPath = EScrollPath.LINEAR) {
        this._options.path = path;
        this._setPath(path);
        this.resetScroll();
    }

    public onChange(funct: (perc: number) => void, scope?: any, first?: boolean) {
        this.on(ScrollEvent.Progress, funct, scope, first);
    }

    private notifyChange(perc: number) {
        this.notify(ScrollEvent.Progress, perc);
    }

    public setScrollEvents() {
        this.browser.addEventListener('scroll', () => {
            this.checkScroll();
        });

        this.browser.addEventListener('pointerup', () => {
            setTimeout(() => {
                const { hash } = window.location;
                if (hash) {
                    const scrollTarget = this.browser.querySelector(hash);
                    if (scrollTarget) {
                        // scrollIntoView not compatible Apple yet (Safari & Iphone)
                        if (scrollTarget.scrollIntoView) {
                            scrollTarget.scrollIntoView();
                        } else if (scrollTarget.scrollIntoViewIfNeeded) {
                            scrollTarget.scrollIntoViewIfNeeded(true);
                        }
                    }
                }
            }, 10);
        });

        this.resetScroll();
    }

    public previewScroll() {
        this.catchScroll(1);
        setTimeout(() => {
            this.catchScroll(0);
        }, 1000);
    }

    public pageScrollPercentage = 0;

    private isScrolling = false;

    private scrollingTimeout;

    private checkScroll() {
        //* Test to change how scroll works
        //* If scroll seems buggy on iOS, it is probably due to low battery mode
        // if (!this.isScrolling) {
        //     this.animation.infinite(() => {
        //         const scroll = this.getScrollPercentage();
        //         this.catchScroll(scroll);
        //     });
        // }
        // this.isScrolling = true;
        // clearTimeout(this.scrollingTimeout);
        // this.scrollingTimeout = setTimeout(() => {
        //     this.animation.stop();
        //     this.isScrolling = false;
        // }, 150);

        const scroll = this.getScrollPercentage();
        this.catchScroll(scroll);
    }

    private scrollGap = 0;

    private start = 0;

    private catchScroll(scroll: number) {
        const { catchSpeed } = this._options;
        if (!this.animation.running) this.notify(ScrollEvent.Start, 0);
        this.start = this.pageScrollPercentage;
        this.scrollGap = scroll - this.start;
        let howmany = (1 - catchSpeed) * 0.2;
        howmany = Math.max(howmany, 1);
        this.animation.simple(howmany, (perc) => {
            this.scrollChange(perc);
        });
    }

    public resetScroll() {
        const scrollHeight = this.checkHeight();
        // End of animation to have correct position
        this.notify(ScrollEvent.Start, 0);
        //* If no scroll don't need to send any event
        if (scrollHeight) this.setPercentage(1);
    }

    private scrollChange(perc: number) {
        const progressGap = perc * this.scrollGap;
        this.pageScrollPercentage = this.start + progressGap;
        this.setPercentage(perc);
    }

    public percentage = 0;

    public setPercentage(percentage: number) {
        this.percentage = percentage;
        this.setGeometryFromPercentage(this.pageScrollPercentage);
        this.notifyChange(percentage);
    }
}
